import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useRolarAoEscolher } from "../lib/rolarPara";
import { IconeOlho } from "./icones";
import {
  alunoDetalhe,
  atualizarCadastro,
  buscarAluno,
  cancelarFonte,
  cancelarSenhaLiberada,
  corrigirPrazo,
  criarAluno,
  dataCurta,
  dataHora,
  fimDoDiaEmBrasilia,
  liberarAcesso,
  liberarPrimeiroAcesso,
  liberarSenhaSemEmail,
  numero,
  produtosDoCatalogo,
  senhaLiberadaDoAluno,
  trocarEmailDoAluno,
  verComo,
  type AlunoEncontrado,
  type Dossie,
  type FonteDeAcesso,
  type ResultadoBusca,
  type SituacaoDaSenhaLiberada,
} from "../lib/admin";
import { Bloco, Campo, Chip, Th, Vazio } from "./painel";

/**
 * Suporte → Alunos: achar o aluno e ver o caso dele.
 *
 * ## Onde esta tela DISCORDA da do Bubble, e por quê
 *
 * A tela "Editar dados de um usuário" do Bubble lista um produto por linha, com
 * uma data de expiração editável em cada. No hub isso não existe: `acessos` tem
 * uma linha por (aluno, produto, TRANSAÇÃO), e 1.877 alunos reais têm duas ou
 * mais fontes vigentes do MESMO produto — quem comprou duas vezes.
 *
 * Copiar a tela do Bubble obrigaria a escolher uma fonte arbitrária para mostrar.
 * O operador mudaria a data, a outra fonte continuaria valendo, o aluno
 * continuaria com acesso, e ninguém entenderia por quê. Então aqui aparecem três
 * blocos separados, na ordem em que o operador pensa:
 *
 *   1. **O que o aluno vê** — por curso, com a regra de contrato já aplicada.
 *      É a resposta de "ele diz que não abre".
 *   2. **Até quando ele tem** — o agregado por produto. Derivado, nunca somado
 *      aqui.
 *   3. **As fontes** — uma linha por linha de `acessos`. É onde a próxima etapa
 *      vai agir, e é o único lugar onde faz sentido mexer numa data.
 *
 * ## Esta tela é só leitura
 *
 * De propósito, e não por etapa faltando: cada botão de escrita chega com a sua
 * migration, o seu registro de quem fez e o seu teste. Botão desabilitado
 * prometendo escrita seria pior que a ausência dele — o operador tentaria usar.
 */
export default function SuporteAlunos({ podeVerComo = false }: { podeVerComo?: boolean }) {
  const [termo, setTermo] = useState("");
  const [busca, setBusca] = useState<ResultadoBusca | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [dossie, setDossie] = useState<Dossie | null>(null);
  const [abrindo, setAbrindo] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);
  // Escolher um aluno da busca abre o caso dele ABAIXO da lista de resultados,
  // fora da tela quando a busca devolve várias linhas. O operador clica e não
  // vê nada acontecer. Achado por Victor em 28/08.
  const dossieRef = useRef<HTMLDivElement | null>(null);
  useRolarAoEscolher(dossieRef, dossie?.perfil.id ?? null);

  // `?perfil=` reabre o caso de alguém. É por onde o «Voltar ao normal» do «ver
  // como» (000335) devolve o master ao dossiê de onde ele saiu — e serve também
  // para mandar o link de um caso a um colega.
  const [params] = useSearchParams();
  const perfilDaUrl = params.get("perfil");
  useEffect(() => {
    if (!perfilDaUrl) return;
    let vivo = true;
    (async () => {
      const d = await alunoDetalhe(perfilDaUrl);
      if (vivo && d) setDossie(d);
    })();
    return () => {
      vivo = false;
    };
  }, [perfilDaUrl]);

  async function pesquisar(e: React.FormEvent) {
    e.preventDefault();
    setBuscando(true);
    setDossie(null);
    setBusca(await buscarAluno(termo));
    setBuscando(false);
  }

  async function abrir(aluno: AlunoEncontrado) {
    setAbrindo(aluno.profile_id);
    setDossie(await alunoDetalhe(aluno.profile_id));
    setAbrindo(null);
  }

  // Devolve o dossiê novo, e não só atualiza o estado: depois de liberar um
  // produto a tela precisa CONFERIR se a fonte apareceu — a RPC devolve o id do
  // evento, não sucesso, e evento pode ter ficado com erro.
  async function recarregar(): Promise<Dossie | null> {
    if (!dossie) return null;
    const novo = await alunoDetalhe(dossie.perfil.id);
    setDossie(novo);
    return novo;
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-bold tracking-widest text-laranja-texto uppercase">Suporte</p>
        <h1 className="mt-1 font-display text-2xl font-bold text-titulo">Alunos</h1>
        <p className="mt-2 max-w-3xl text-base leading-relaxed text-cinza">
          Ache o aluno e veja o caso dele inteiro — o que ele vê na tela, até quando
          tem acesso, de que compra cada acesso veio e o que a esteira tentou fazer.
        </p>
      </div>

      <form onSubmit={pesquisar} className="surface p-4">
        <label htmlFor="busca-aluno" className="text-sm font-semibold text-titulo">
          Pesquise por e-mail, nome, CPF ou id do Bubble
        </label>
        <div className="mt-2 flex flex-wrap gap-2">
          <input
            id="busca-aluno"
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            placeholder="maria@exemplo.com"
            autoComplete="off"
            className="min-w-0 flex-1 rounded-cf border border-borda bg-superficie px-3 py-2.5 text-sm text-tinta placeholder:text-cinza/70"
          />
          <button type="submit" disabled={buscando} className="pill-cta">
            {buscando ? "Procurando…" : "Procurar"}
          </button>
        </div>
        {/* O CPF casa por dígitos dos dois lados, então colar do documento
            funciona — e dizer isso economiza a tentativa frustrada. */}
        <p className="mt-2 text-xs leading-relaxed text-cinza">
          Mínimo de 3 caracteres. O CPF pode vir com pontos e traço. O id do Bubble
          é a costura do dual-run: cole o <code>bubble:…</code> e caia na linha
          correspondente aqui.
        </p>
        <div className="mt-3 border-t border-borda pt-3">
          <button
            type="button"
            onClick={() => setCriando((c) => !c)}
            className="text-sm font-semibold text-link underline decoration-laranja/50 underline-offset-2"
          >
            {criando ? "Fechar" : "Não achou? Criar cadastro"}
          </button>
        </div>
      </form>

      {criando && (
        <CriarCadastro
          onCriado={async (profileId, jaExistia) => {
            setCriando(false);
            setBusca(null);
            setDossie(await alunoDetalhe(profileId));
            return jaExistia;
          }}
        />
      )}

      {busca?.fase === "recusado" && (
        <p className="rounded-cf bg-erro-fundo px-4 py-3 text-sm text-erro-tinta">{busca.mensagem}</p>
      )}
      {busca?.fase === "erro" && (
        <p className="rounded-cf bg-erro-fundo px-4 py-3 text-sm text-erro-tinta">
          A busca falhou. Nenhum resultado é mostrado no lugar.
        </p>
      )}

      {busca?.fase === "ok" && (
        <Resultados
          itens={busca.itens}
          total={busca.total}
          abrindo={abrindo}
          selecionado={dossie?.perfil.id ?? null}
          onAbrir={abrir}
        />
      )}

      {dossie && (
        <div ref={dossieRef}>
          <DossieDoAluno dossie={dossie} recarregar={recarregar} podeVerComo={podeVerComo} />
        </div>
      )}
    </div>
  );
}

function Resultados({
  itens,
  total,
  abrindo,
  selecionado,
  onAbrir,
}: {
  itens: AlunoEncontrado[];
  total: number;
  abrindo: string | null;
  selecionado: string | null;
  onAbrir: (a: AlunoEncontrado) => void;
}) {
  if (itens.length === 0) {
    return (
      <p className="surface px-4 py-3 text-sm text-cinza">
        Nenhum aluno com esse termo. Vale tentar só o começo do e-mail — a busca
        casa pedaço.
      </p>
    );
  }

  return (
    <section>
      <div className="flex flex-wrap items-baseline gap-2">
        <h2 className="font-display text-base font-bold text-titulo">
          {numero(itens.length)} {itens.length === 1 ? "aluno" : "alunos"}
        </h2>
        {/* Corte que se anuncia. A cicatriz é do próprio projeto: a Data API do
            Bubble para em 50.000 e diz que acabou, e a primeira extração trouxe
            metade dos usuários parecendo completa. */}
        {total > itens.length && (
          <p className="text-sm font-semibold text-laranja-texto">
            de {numero(total)} — refine a busca para ver o resto
          </p>
        )}
      </div>

      <ul className="mt-3 space-y-2">
        {itens.map((a) => (
          <li key={a.profile_id}>
            <button
              type="button"
              onClick={() => onAbrir(a)}
              aria-current={selecionado === a.profile_id ? "true" : undefined}
              className={`surface porta w-full p-3 text-left ${
                selecionado === a.profile_id ? "border-laranja/60" : ""
              }`}
            >
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-semibold text-titulo">{a.nome ?? "sem nome"}</span>
                <span className="text-sm text-cinza">{a.email}</span>
                {abrindo === a.profile_id && (
                  <span className="text-xs font-semibold text-laranja-texto">abrindo…</span>
                )}
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                <Chip>{a.cursos_vigentes} curso(s) vigente(s)</Chip>
                {/* Fontes ≠ cursos, e o rótulo diz isso: fonte é compra. Sem
                    explicitar, "4 fontes / 2 cursos" lê como duplicidade. */}
                <Chip>{a.acessos_vigentes} fonte(s) de acesso</Chip>
                {a.tem_conta ? (
                  <Chip tom={a.email_confirmado ? "ok" : "atencao"}>
                    {a.email_confirmado ? "conta confirmada" : "conta não confirmada"}
                  </Chip>
                ) : (
                  <Chip tom="atencao">sem conta no hub</Chip>
                )}
                {a.cpf && <Chip>CPF {a.cpf}</Chip>}
              </div>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** A ação aberta no formulário. Uma por vez, de propósito: escrita sobre acesso
 *  não é coisa de fazer em lote sem olhar. */
type Acao =
  | { tipo: "prazo"; fonte: FonteDeAcesso }
  | { tipo: "cancelar"; fonte: FonteDeAcesso }
  | { tipo: "liberar" }
  | { tipo: "cadastro" }
  | { tipo: "primeiro-acesso" }
  | { tipo: "senha-liberada" }
  | { tipo: "cancelar-senha" }
  | { tipo: "trocar-email" };

type Recado = { tom: "ok" | "atencao" | "erro"; texto: string };

function DossieDoAluno({
  dossie,
  recarregar,
  podeVerComo,
}: {
  dossie: Dossie;
  recarregar: () => Promise<Dossie | null>;
  podeVerComo: boolean;
}) {
  const p = dossie.perfil;
  const [acao, setAcao] = useState<Acao | null>(null);
  const [recado, setRecado] = useState<Recado | null>(null);
  const [abrindoVerComo, setAbrindoVerComo] = useState(false);

  // A senha sem e-mail (000383) mora fora do dossiê, numa leitura própria.
  // Relida a cada dossiê novo: é o que acontece depois de liberar ou cancelar.
  const [senhaLiberada, setSenhaLiberada] = useState<SituacaoDaSenhaLiberada | undefined>(undefined);
  useEffect(() => {
    let vivo = true;
    void senhaLiberadaDoAluno(p.id).then((s) => {
      if (vivo) setSenhaLiberada(s);
    });
    return () => {
      vivo = false;
    };
  }, [dossie, p.id]);

  function fechar(r: Recado | null) {
    setAcao(null);
    if (r) setRecado(r);
  }

  // O «ver como» (000335). No caminho feliz a página recarrega já como a outra
  // pessoa, e esta função não volta; ela só volta com a recusa do banco.
  async function abrirVerComo() {
    setAbrindoVerComo(true);
    const r = await verComo(p.id, `/admin/alunos?perfil=${encodeURIComponent(p.id)}`);
    if (r) {
      setRecado({ tom: "erro", texto: r.mensagem });
      setAbrindoVerComo(false);
    }
  }

  return (
    <div className="space-y-6 border-t border-borda pt-6">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2 className="font-display text-xl font-bold text-titulo">{p.nome ?? "sem nome"}</h2>
          <p className="text-sm text-cinza">lido às {dataHora(dossie.lido_em)}</p>
        </div>
        {/* Só para o MASTER, e o desenho não é a regra: quem recusa é
            `ver_como_iniciar`, pela conta logada. */}
        {podeVerComo && (
          <button
            type="button"
            onClick={abrirVerComo}
            disabled={abrindoVerComo}
            className="inline-flex shrink-0 items-center gap-2 rounded-full border border-acento/40 bg-superficie px-4 py-2 text-sm font-semibold text-acento hover:border-acento disabled:opacity-60"
            data-ver-como-abrir=""
          >
            <IconeOlho className="size-4" />
            {abrindoVerComo ? "Abrindo…" : "Ver como"}
          </button>
        )}
      </div>
      {podeVerComo && (
        <p className="-mt-4 text-xs leading-relaxed text-cinza">
          «Ver como» abre o hub como esta pessoa vê, por até uma hora e só para leitura:
          nada é gravado, e o que abre outro sistema (aulas, IA, emissão) fica desligado.
          Fica registrado quem viu e quando.
        </p>
      )}

      {recado && (
        <p
          className={`rounded-cf px-4 py-3 text-sm leading-relaxed ${
            recado.tom === "erro"
              ? "bg-erro-fundo text-erro-tinta"
              : recado.tom === "atencao"
                ? "bg-alerta-fundo text-alerta-tinta"
                : "bg-aviso-fundo text-aviso-tinta"
          }`}
        >
          {recado.texto}
        </p>
      )}

      {acao && (
        <FormularioDeAcao
          acao={acao}
          dossie={dossie}
          onFim={async (r) => {
            fechar(r);
            if (r?.tom !== "erro") await recarregar();
          }}
          recarregar={recarregar}
        />
      )}

      <Bloco
        titulo="Cadastro"
        descricao="CPF é editável pelo Suporte por decisão de 17/08 — o aluno não muda o dele. E-mail ainda não: trocar só aqui faria uma venda futura com o e-mail antigo criar um segundo perfil."
      >
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
          <Campo rotulo="E-mail" valor={p.email} />
          <Campo rotulo="CPF" valor={p.cpf ?? "—"} />
          <Campo rotulo="Telefone" valor={p.telefone ?? "—"} />
          <Campo
            rotulo="Conta no hub"
            valor={
              !p.tem_conta
                ? "não criou conta"
                : p.email_confirmed_at
                  ? `confirmada em ${dataCurta(p.email_confirmed_at)}`
                  : "criada, e-mail não confirmado"
            }
          />
          <Campo rotulo="Último acesso" valor={dataHora(p.last_sign_in_at)} />
          <Campo
            rotulo="Senha no Memberkit"
            valor={p.mk_senha_em ? `espelhada em ${dataCurta(p.mk_senha_em)}` : "pendente"}
          />
          <Campo rotulo="Senha sem e-mail" valor={textoDaSenhaLiberada(senhaLiberada)} />
          <Campo rotulo="Cadastrado em" valor={dataCurta(p.criado_em)} />
          <Campo rotulo="Id no Bubble" valor={p.bubble_ref ?? "—"} />
          {/* O único papel que sobrou (000297). O acesso de quem não é master
              é por grupo, e mora em Admin › Permissões — repeti-lo aqui seria a
              segunda tela para a mesma pergunta. */}
          <Campo
            rotulo="Master"
            valor={dossie.papeis.includes("admin") ? "sim — edita todas as abas do painel" : "não"}
          />
        </dl>

        <div className="mt-4 flex flex-wrap gap-2 border-t border-borda pt-4">
          <button
            type="button"
            onClick={() => {
              setRecado(null);
              setAcao({ tipo: "cadastro" });
            }}
            className="btn-contorno"
          >
            Editar nome, telefone e CPF
          </button>
          <button
            type="button"
            onClick={() => {
              setRecado(null);
              setAcao({ tipo: "trocar-email" });
            }}
            className="btn-contorno"
          >
            Trocar e-mail
          </button>
          <button
            type="button"
            onClick={() => {
              setRecado(null);
              setAcao({ tipo: "primeiro-acesso" });
            }}
            className="btn-contorno"
          >
            Liberar primeiro acesso
          </button>
          <button
            type="button"
            onClick={() => {
              setRecado(null);
              setAcao({ tipo: "senha-liberada" });
            }}
            className="btn-contorno"
          >
            Liberar senha sem e-mail
          </button>
          {senhaLiberada?.valendo && (
            <button
              type="button"
              onClick={() => {
                setRecado(null);
                setAcao({ tipo: "cancelar-senha" });
              }}
              className="btn-contorno"
            >
              Cancelar a liberação da senha
            </button>
          )}
          {/* NÃO há mais "Definir papéis de operador" (000297): o papel Suporte
              morreu, e o master e os grupos se dão em Admin › Permissões — uma
              porta só para quem entra no painel. */}
        </div>
      </Bloco>

      <Bloco
        titulo="O que o aluno vê"
        descricao="Por curso, com a regra de contrato já aplicada pelo banco. É a resposta de “ele diz que não abre”."
      >
        {dossie.cursos.length === 0 ? (
          <Vazio>Nenhum curso vigente. Se ele diz que comprou, a resposta está em Eventos.</Vazio>
        ) : (
          <ul className="space-y-2">
            {dossie.cursos.map((c) => (
              <li key={c.curso_slug} className="rounded-cf bg-superficie-2 p-3">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-semibold text-titulo">{c.curso_nome}</span>
                  <span className="text-sm tabular-nums text-cinza">
                    {c.vitalicio ? "vitalício" : `até ${dataCurta(c.fim_efetivo)}`}
                  </span>
                  {c.trancado && <Chip tom="atencao">vitrine trancada — contrato pendente</Chip>}
                  {c.faculdade && <Chip>faculdade</Chip>}
                </div>
                <p className="mt-1 text-xs text-cinza">
                  {c.fontes} fonte(s) · {c.produtos.join(", ")}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Bloco>

      <Bloco
        titulo="Até quando ele tem"
        descricao="O agregado por produto, derivado das fontes vigentes. Nulo em vitalício é data nula, nunca data grande."
      >
        {dossie.efetivo.length === 0 ? (
          <Vazio>Nenhum acesso vigente.</Vazio>
        ) : (
          <ul className="space-y-1.5">
            {dossie.efetivo.map((e) => (
              <li key={e.produto_slug} className="flex flex-wrap items-baseline gap-x-3 text-sm">
                <span className="font-semibold text-titulo">{e.produto_nome}</span>
                <span className="tabular-nums text-cinza">
                  {e.vitalicio ? "vitalício" : `até ${dataCurta(e.fim_efetivo)}`}
                </span>
                <span className="text-xs text-cinza">
                  {e.fontes} fonte(s) sustentando
                </span>
              </li>
            ))}
          </ul>
        )}
      </Bloco>

      <Bloco
        titulo="As fontes"
        descricao="Uma linha por compra, inclusive expiradas e canceladas. Duas linhas do mesmo produto não são duplicidade: são duas compras, cada uma com a sua vigência — e é aqui que faz sentido mexer numa data."
      >
        {dossie.fontes.length === 0 ? (
          <Vazio>Nenhuma fonte de acesso.</Vazio>
        ) : (
          <TabelaFontes
            fontes={dossie.fontes}
            onCorrigirPrazo={(f) => {
              setRecado(null);
              setAcao({ tipo: "prazo", fonte: f });
            }}
            onCancelar={(f) => {
              setRecado(null);
              setAcao({ tipo: "cancelar", fonte: f });
            }}
          />
        )}
        <div className="mt-4 border-t border-borda pt-4">
          <button
            type="button"
            onClick={() => {
              setRecado(null);
              setAcao({ tipo: "liberar" });
            }}
            className="btn-contorno"
          >
            Liberar um produto à mão
          </button>
          <p className="mt-2 text-xs leading-relaxed text-cinza">
            Entra como fonte de origem <code>manual</code>, com motivo obrigatório, e
            fica na mesma auditoria das vendas. Reentrega de webhook nunca altera
            acesso manual.
          </p>
        </div>
      </Bloco>

      {dossie.matriculas.length > 0 && (
        <Bloco titulo="Matrículas" descricao="Pós-graduação e MBA, na secretaria da faculdade.">
          <ul className="space-y-1.5 text-sm">
            {dossie.matriculas.map((m) => (
              <li key={m.id} className="flex flex-wrap items-baseline gap-x-3">
                <span className="font-semibold text-titulo">{m.produto_nome}</span>
                <span className="text-cinza">{m.turma ?? "sem turma"}</span>
                <span className="tabular-nums text-cinza">
                  matrícula {dataCurta(m.inicio)}
                  {m.previsao_termino ? ` · previsão ${dataCurta(m.previsao_termino)}` : ""}
                </span>
                <Chip tom={m.status === "ativa" ? "ok" : "neutro"}>{m.status}</Chip>
              </li>
            ))}
          </ul>
        </Bloco>
      )}

      <Bloco
        titulo="Contratos"
        descricao="A assinatura vale por CURSO, não por SKU: assinar em qualquer produto do curso destranca o conjunto."
      >
        {dossie.contratos.length === 0 && dossie.a_assinar.length === 0 ? (
          <Vazio>Nenhum contrato registrado.</Vazio>
        ) : (
          <>
            {dossie.contratos.length > 0 && (
              <ul className="space-y-1.5 text-sm">
                {dossie.contratos.map((c) => (
                  <li key={c.produto_slug} className="flex flex-wrap items-baseline gap-x-3">
                    <span className="font-semibold text-titulo">{c.produto_nome}</span>
                    <Chip tom={c.status === "assinado" ? "ok" : "atencao"}>{c.status}</Chip>
                    {c.assinado_em && (
                      <span className="tabular-nums text-cinza">em {dataCurta(c.assinado_em)}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {dossie.a_assinar.length > 0 && (
              <div className={dossie.contratos.length > 0 ? "mt-3 border-t border-borda pt-3" : ""}>
                <p className="text-xs font-bold tracking-wide text-cinza uppercase">
                  O contrato que ele precisa assinar
                </p>
                <ul className="mt-1.5 space-y-1.5 text-sm">
                  {dossie.a_assinar.map((v) => (
                    <li key={v.produto_slug} className="flex flex-wrap items-baseline gap-x-3">
                      <span className="font-semibold text-titulo">{v.produto_nome}</span>
                      <span className="text-cinza">
                        {v.variante ?? "padrão"}
                        {v.de_variante ? " (da oferta que ele comprou)" : ""}
                      </span>
                      {!v.contrato_url && <Chip tom="atencao">sem link</Chip>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </Bloco>

      <Bloco
        titulo="Eventos"
        descricao="O que a esteira tentou fazer com o e-mail dele. Evento com erro é venda que NÃO entrou — é aqui que mora a resposta de “paguei e não liberou”."
      >
        {dossie.eventos.length === 0 ? (
          <Vazio>
            Nenhum evento. Se ele tem acesso, veio da carga do Bubble, não de venda pela esteira.
          </Vazio>
        ) : (
          <ul className="space-y-2">
            {dossie.eventos.map((ev) => {
              const aviso = ev.erro?.startsWith("aviso:") ?? false;
              const falhou = ev.erro !== null && !aviso;
              return (
                <li
                  key={ev.id}
                  className={`rounded-cf p-3 text-sm ${
                    falhou ? "bg-erro-fundo" : aviso ? "bg-alerta-fundo" : "bg-superficie-2"
                  }`}
                >
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="font-semibold text-titulo">{ev.tipo}</span>
                    <span className="text-cinza">{ev.origem}</span>
                    <span className="tabular-nums text-cinza">{dataHora(ev.recebido_em)}</span>
                    {ev.processado_em ? (
                      <Chip tom={aviso ? "atencao" : "ok"}>
                        {aviso ? "aplicado com divergência" : "aplicado"}
                      </Chip>
                    ) : (
                      <Chip tom="erro">não aplicado</Chip>
                    )}
                  </div>
                  {ev.erro && (
                    <p className={`mt-1 ${falhou ? "text-erro-tinta" : "text-alerta-tinta"}`}>
                      {ev.erro}
                    </p>
                  )}
                  {ev.motivo && <p className="mt-1 text-cinza">motivo: {ev.motivo}</p>}
                  <p className="mt-1 text-xs text-cinza">transação {ev.id_transacao}</p>
                </li>
              );
            })}
          </ul>
        )}
      </Bloco>

      {dossie.correcoes.length > 0 && (
        <Bloco
          titulo="Correções de prazo"
          descricao="Prazo mexido à mão. O valor anterior fica guardado — é o que torna uma redução de acesso reversível."
        >
          <ul className="space-y-1.5 text-sm">
            {dossie.correcoes.map((c) => (
              <li key={c.id} className="flex flex-wrap items-baseline gap-x-3">
                <span className="font-semibold text-titulo">{c.produto_slug ?? "—"}</span>
                <span className="tabular-nums text-cinza">
                  {c.vitalicio_antes ? "vitalício" : dataCurta(c.fim_antes)} →{" "}
                  {c.vitalicio_depois ? "vitalício" : dataCurta(c.fim_depois)}
                </span>
                <span className="text-cinza">{c.motivo}</span>
                <span className="text-xs tabular-nums text-cinza">{dataCurta(c.corrigido_em)}</span>
              </li>
            ))}
          </ul>
        </Bloco>
      )}

      <p className="text-sm leading-relaxed text-cinza">
        Prazo, cancelamento e liberação já funcionam, cada um com motivo obrigatório
        e registro de quem fez. Ainda não: editar cadastro (nome, e-mail, CPF),
        papéis, criar usuário e liberar primeiro acesso — para essas, o caminho é o
        painel do Bubble, que continua ligado.
      </p>
    </div>
  );
}

/**
 * As fontes em tabela, e não em cards.
 *
 * Aqui o operador COMPARA datas entre linhas do mesmo produto — é literalmente a
 * tarefa —, e comparar número entre cards empilhados obriga a guardar valor na
 * memória. A tabela rola na horizontal no celular em vez de quebrar coluna.
 */
function TabelaFontes({
  fontes,
  onCorrigirPrazo,
  onCancelar,
}: {
  fontes: FonteDeAcesso[];
  onCorrigirPrazo: (f: FonteDeAcesso) => void;
  onCancelar: (f: FonteDeAcesso) => void;
}) {
  const tomDoStatus = (s: FonteDeAcesso["status"]) =>
    s === "vigente" ? "ok" : s === "cancelado" ? "erro" : "neutro";

  return (
    <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
      <table className="w-full min-w-[52rem] border-collapse text-sm">
        <thead>
          <tr className="border-b border-borda text-left">
            <Th>Produto</Th>
            <Th>Origem</Th>
            <Th>Compra</Th>
            <Th>Início</Th>
            <Th>Fim</Th>
            <Th>Situação</Th>
            <Th>Ações</Th>
          </tr>
        </thead>
        <tbody>
          {fontes.map((f) => (
            <tr key={f.acesso_id} className="border-b border-borda/60 last:border-0">
              <td className="py-2.5 pr-3 align-top">
                <span className="font-semibold text-titulo">{f.produto_nome}</span>
                {f.contrato_variante && (
                  <span className="block text-xs text-cinza">oferta {f.contrato_variante}</span>
                )}
              </td>
              <td className="py-2.5 pr-3 align-top text-cinza">{f.origem}</td>
              {/* A transação é o que liga a linha à venda. Sem ela o operador não
                  consegue decidir qual fonte mexer. */}
              <td className="py-2.5 pr-3 align-top">
                <code className="text-xs break-all text-cinza">{f.transacao_origem}</code>
                {f.bubble_ref && (
                  <span className="block text-xs text-cinza">veio da carga do Bubble</span>
                )}
              </td>
              <td className="py-2.5 pr-3 align-top tabular-nums text-cinza">
                {dataCurta(f.inicio)}
              </td>
              <td className="py-2.5 pr-3 align-top tabular-nums text-cinza">
                {/* Vitalício é data NULA. Mostrar "—" aqui faria parecer dado
                    faltando, que é o oposto do que significa. */}
                {f.vitalicio ? "vitalício" : dataCurta(f.fim)}
              </td>
              <td className="py-2.5 pr-3 align-top">
                <Chip tom={tomDoStatus(f.status)}>{f.status}</Chip>
              </td>
              <td className="py-2.5 align-top">
                {/* Fonte já cancelada não ganha botões: corrigir o prazo de um
                    acesso cancelado não devolve acesso a ninguém e só produziria
                    um registro confuso na auditoria. */}
                {f.status === "cancelado" ? (
                  <span className="text-xs text-cinza">—</span>
                ) : (
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => onCorrigirPrazo(f)}
                      className="text-left text-xs font-semibold text-link underline decoration-laranja/50 underline-offset-2 hover:text-laranja-texto"
                    >
                      Corrigir prazo
                    </button>
                    <button
                      type="button"
                      onClick={() => onCancelar(f)}
                      className="text-left text-xs font-semibold text-erro-tinta underline decoration-erro-tinta/40 underline-offset-2"
                    >
                      Cancelar esta fonte
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * O formulário da ação escolhida.
 *
 * Um só componente para as três porque as três compartilham a parte que mais
 * importa: **o motivo é obrigatório**, e o banco recusa sem ele. Não é validação
 * de formulário — é o que faz a auditoria servir para alguma coisa seis meses
 * depois, quando alguém pergunta por que aquele aluno tem cinco anos de acesso.
 */
function FormularioDeAcao({
  acao,
  dossie,
  onFim,
  recarregar,
}: {
  acao: Acao;
  dossie: Dossie;
  onFim: (r: Recado | null) => void | Promise<void>;
  recarregar: () => Promise<Dossie | null>;
}) {
  const [motivo, setMotivo] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Estado só do formulário de prazo.
  // Só duas ações agem sobre UMA fonte; as outras são sobre o aluno. A
  // lista positiva (em vez de "tudo menos liberar") é o que faz o TypeScript
  // reclamar quando uma ação nova entrar, em vez de deixar `fonte` indefinida em
  // silêncio.
  const fonte = acao.tipo === "prazo" || acao.tipo === "cancelar" ? acao.fonte : null;
  const [vitalicio, setVitalicio] = useState(fonte?.vitalicio ?? false);
  const [data, setData] = useState(fonte?.fim ? fonte.fim.slice(0, 10) : "");

  // Estado só do formulário de liberação.
  const [produtos, setProdutos] = useState<Array<{ slug: string; nome: string }>>([]);
  const [slug, setSlug] = useState("");
  useEffect(() => {
    if (acao.tipo === "liberar") void produtosDoCatalogo().then(setProdutos);
  }, [acao.tipo]);

  // Estado só do formulário de cadastro. Começa com o valor atual: assim o campo
  // que o operador não mexer chega igual e a RPC não registra mudança nenhuma —
  // é o que mantém a auditoria só com o que de fato mudou.
  const [nome, setNome] = useState(dossie.perfil.nome ?? "");
  const [telefone, setTelefone] = useState(dossie.perfil.telefone ?? "");
  const [cpf, setCpf] = useState(dossie.perfil.cpf ?? "");

  // Estado só da troca de e-mail. Começa VAZIO, ao contrário do cadastro: aqui o
  // campo pré-preenchido com o e-mail atual convidaria a editar uma letra e
  // salvar sem reler — e o que se troca é a chave que liga o aluno às compras
  // dele. Digitar o endereço inteiro é a confirmação.
  const [emailNovo, setEmailNovo] = useState("");

  /**
   * Quantas fontes sustentam o produto desta fonte, segundo o BANCO.
   *
   * Sai de `dossie.efetivo`, que é `vw_acesso_efetivo` — não é recontagem no
   * JavaScript. A diferença importa: uma fonte com status `vigente` e data no
   * passado não sustenta nada, e recontar as linhas da tabela diria que sustenta.
   */
  const sustentando =
    fonte ? (dossie.efetivo.find((e) => e.produto_slug === fonte.produto_slug)?.fontes ?? 0) : 0;
  const ehAUltima = sustentando === 1;

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setSalvando(true);

    if (acao.tipo === "prazo") {
      const r = await corrigirPrazo(
        acao.fonte.acesso_id,
        vitalicio ? null : data ? fimDoDiaEmBrasilia(data) : null,
        vitalicio,
        motivo,
      );
      setSalvando(false);
      if (!r.ok) return setErro(r.mensagem);
      const antes = r.res.vitalicio_antes ? "vitalício" : dataCurta(r.res.fim_antes);
      const depois = r.res.vitalicio_depois ? "vitalício" : dataCurta(r.res.fim_depois);
      await onFim({
        tom: r.res.reduziu ? "atencao" : "ok",
        texto: r.res.reduziu
          ? `Prazo de ${r.res.produto_slug} REDUZIDO de ${antes} para ${depois}. O valor anterior está guardado — dá para voltar atrás. Se o aluno já usava esse acesso, ele precisa ser avisado.`
          : `Prazo de ${r.res.produto_slug} alterado de ${antes} para ${depois}.`,
      });
      return;
    }

    if (acao.tipo === "cancelar") {
      const r = await cancelarFonte(acao.fonte.acesso_id, motivo);
      setSalvando(false);
      if (!r.ok) return setErro(r.mensagem);
      if (!r.res.mudou) {
        await onFim({ tom: "atencao", texto: r.res.motivo_do_nao ?? "Nada mudou." });
        return;
      }
      // A consequência vem do banco, medida DEPOIS do cancelamento. É a
      // diferença entre uma correção e uma reclamação de aluno.
      const perdeu = r.res.perdeu_o_produto;
      await onFim({
        tom: perdeu ? "atencao" : "ok",
        texto: perdeu
          ? `Fonte cancelada, e o aluno PERDEU o acesso a ${r.res.produto_slug}: não sobrou nenhuma outra compra sustentando.${
              r.res.era_vitalicio ? " Era um acesso vitalício." : ""
            } A linha não foi apagada — dá para reverter.`
          : `Fonte cancelada. O aluno CONTINUA com ${r.res.produto_slug}: ${r.res.fontes_restantes} outra(s) compra(s) sustenta(m) o acesso${
              r.res.vitalicio_restante
                ? " (vitalício)"
                : r.res.fim_efetivo_restante
                  ? `, até ${dataCurta(r.res.fim_efetivo_restante)}`
                  : ""
            }.`,
      });
      return;
    }

    if (acao.tipo === "cadastro") {
      const r = await atualizarCadastro(
        dossie.perfil.id,
        { nome, telefone, cpf },
        motivo,
      );
      setSalvando(false);
      if (!r.ok) return setErro(r.mensagem);
      const campos = Object.keys(r.res.campos);
      await onFim({
        tom: "ok",
        texto: r.res.mudou
          ? `Cadastro atualizado: ${campos.join(", ")}.`
          : "Nada mudou — os valores enviados eram os que já estavam gravados.",
      });
      return;
    }

    if (acao.tipo === "trocar-email") {
      const r = await trocarEmailDoAluno(dossie.perfil.id, emailNovo.trim(), motivo);
      setSalvando(false);
      if (!r.ok) return setErro(r.mensagem);
      await onFim({
        // Tom de ATENÇÃO, não de "ok": a troca deu certo, e mesmo assim sobra
        // trabalho fora do hub. Dizer só "pronto" faria o operador fechar a tela
        // achando que acabou, e o aluno descobriria nas aulas.
        tom: "atencao",
        texto:
          `E-mail trocado de ${r.res.de} para ${r.res.para}.` +
          (r.res.resultado === "trocado"
            ? " A conta de login já usa o endereço novo, e o Auth avisou o endereço anterior."
            : " Ele ainda não tem conta no portal; quando criar, será com o endereço novo.") +
          " As AULAS (Memberkit) continuam no endereço anterior — isso precisa ser ajustado lá à mão.",
      });
      return;
    }

    if (acao.tipo === "senha-liberada") {
      const r = await liberarSenhaSemEmail(dossie.perfil.id, motivo);
      setSalvando(false);
      if (!r.ok) return setErro(r.mensagem);
      await onFim(
        r.res.ja_estava
          ? {
              tom: "atencao",
              texto: `Já estava liberado até ${dataHora(r.res.vale_ate)}; nada mudou. Para outra hora, cancele e libere de novo.`,
            }
          : {
              tom: "ok",
              texto: `Liberado até ${dataHora(r.res.vale_ate)}, uma vez só. Peça ao aluno para abrir ${enderecoDaSenhaLiberada()} e criar a senha com o e-mail ${dossie.perfil.email}.`,
            },
      );
      return;
    }

    if (acao.tipo === "cancelar-senha") {
      const r = await cancelarSenhaLiberada(dossie.perfil.id, motivo);
      setSalvando(false);
      if (!r.ok) return setErro(r.mensagem);
      await onFim(
        r.res.cancelada
          ? { tom: "ok", texto: "Liberação cancelada. O aluno não cria mais a senha sem e-mail." }
          : { tom: "atencao", texto: r.res.motivo_do_nao ?? "Nada mudou." },
      );
      return;
    }

    if (acao.tipo === "primeiro-acesso") {
      const r = await liberarPrimeiroAcesso(dossie.perfil.id, motivo);
      setSalvando(false);
      if (!r.ok) return setErro(r.mensagem);
      await onFim({
        tom: "ok",
        texto:
          r.res.resultado === "convite_enviado"
            ? `Convite enviado para ${dossie.perfil.email}. Ele cria a senha pelo link do e-mail.`
            : `Link de redefinição enviado para ${dossie.perfil.email}. O link vai para a caixa dele, não aparece aqui.`,
      });
      return;
    }

    // Liberar
    const r = await liberarAcesso(dossie.perfil.email, slug, motivo);
    if (!r.ok) {
      setSalvando(false);
      return setErro(r.mensagem);
    }
    // A RPC devolve o id do evento, não sucesso. Quem confere é isto: o dossiê
    // novo tem fonte desse produto?
    const novo = await recarregar();
    setSalvando(false);
    const entrou = (novo?.fontes ?? []).some(
      (f) => f.produto_slug === slug && f.status === "vigente",
    );
    await onFim(
      entrou
        ? { tom: "ok", texto: `Acesso a ${slug} liberado como fonte manual.` }
        : {
            tom: "atencao",
            texto: `A liberação foi registrada (evento ${r.res}) mas o acesso não apareceu. O evento ficou na fila de triagem — a causa mais comum é o produto não estar no catálogo com esse slug.`,
          },
    );
  }

  const titulos = {
    prazo: "Corrigir o prazo desta fonte",
    cancelar: "Cancelar esta fonte de acesso",
    liberar: "Liberar um produto à mão",
    cadastro: "Editar o cadastro",
    "primeiro-acesso": "Liberar o primeiro acesso",
    "senha-liberada": "Liberar a senha sem e-mail",
    "cancelar-senha": "Cancelar a liberação da senha",
    "trocar-email": "Trocar o e-mail do aluno",
  };

  return (
    <form onSubmit={enviar} className="surface border-laranja/40 p-4 sm:p-5">
      <h3 className="font-display text-base font-bold text-titulo">{titulos[acao.tipo]}</h3>

      {fonte && (
        <p className="mt-1 text-sm text-cinza">
          {fonte.produto_nome} · compra <code className="break-all">{fonte.transacao_origem}</code> ·{" "}
          {fonte.vitalicio ? "vitalício" : `até ${dataCurta(fonte.fim)}`}
        </p>
      )}

      {acao.tipo === "prazo" && (
        <div className="mt-4 flex flex-wrap items-end gap-4">
          <div>
            <label htmlFor="novo-fim" className="block text-xs font-bold tracking-wide text-cinza uppercase">
              Nova data de fim
            </label>
            <input
              id="novo-fim"
              type="date"
              value={data}
              disabled={vitalicio}
              onChange={(e) => setData(e.target.value)}
              className="mt-1 rounded-cf border border-borda bg-superficie px-3 py-2 text-sm text-tinta disabled:opacity-50"
            />
            {/* O operador precisa saber que o dia digitado vale inteiro, senão
                "some um dia antes" vira ticket. */}
            <p className="mt-1 text-xs text-cinza">O dia digitado vale até 23:59.</p>
          </div>
          <label className="flex items-center gap-2 pb-6 text-sm font-semibold text-titulo">
            <input
              type="checkbox"
              checked={vitalicio}
              onChange={(e) => setVitalicio(e.target.checked)}
              className="size-4"
            />
            Vitalício
          </label>
        </div>
      )}

      {acao.tipo === "prazo" && vitalicio && (
        <p className="mt-1 text-xs leading-relaxed text-cinza">
          Vitalício grava data <strong>nula</strong>, não uma data grande. Data de 200
          anos é o dado sujo do legado, e o banco recusa.
        </p>
      )}

      {acao.tipo === "cancelar" && (
        <p
          className={`mt-3 rounded-cf px-3 py-2 text-sm leading-relaxed ${
            ehAUltima ? "bg-erro-fundo text-erro-tinta" : "bg-alerta-fundo text-alerta-tinta"
          }`}
        >
          {ehAUltima
            ? `Esta é a ÚNICA compra sustentando ${fonte?.produto_nome}. Cancelar tira o acesso do aluno.`
            : `Há ${sustentando} compras sustentando ${fonte?.produto_nome}. Cancelar esta provavelmente NÃO tira o acesso — a confirmação vem do banco depois.`}
          {fonte?.vitalicio && " O acesso é vitalício."}
        </p>
      )}

      {acao.tipo === "cadastro" && (
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <CampoTexto rotulo="Nome" id="ed-nome" valor={nome} onMuda={setNome} />
          <CampoTexto rotulo="Telefone" id="ed-tel" valor={telefone} onMuda={setTelefone} dica="Vazio apaga." />
          <CampoTexto
            rotulo="CPF"
            id="ed-cpf"
            valor={cpf}
            onMuda={setCpf}
            dica="Com ou sem pontuação. Os dígitos são conferidos."
          />
        </div>
      )}

      {acao.tipo === "trocar-email" && (
        <>
          <div className="mt-4 max-w-md">
            <label
              htmlFor="email-novo"
              className="block text-xs font-bold tracking-wide text-cinza uppercase"
            >
              Novo e-mail
            </label>
            <input
              id="email-novo"
              type="email"
              value={emailNovo}
              onChange={(e) => setEmailNovo(e.target.value)}
              required
              autoComplete="off"
              placeholder="digite o endereço completo"
              className="mt-1 w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-sm text-tinta"
            />
            <p className="mt-1 text-xs text-cinza">
              Atual: <code className="break-all">{dossie.perfil.email}</code>
            </p>
          </div>
          {/* O que a troca resolve e o que ela NÃO resolve. Sem isto o operador
              fecha a tela achando que acabou — e o que sobra só aparece quando o
              aluno reclama que as aulas não abrem. */}
          <p className="mt-3 rounded-cf bg-alerta-fundo px-3 py-2 text-sm leading-relaxed text-alerta-tinta">
            O e-mail é a chave que liga o aluno às compras dele. A troca leva junto o
            cadastro, as compras futuras que chegarem com o endereço antigo e
            {dossie.perfil.tem_conta ? " a conta de login" : " o cadastro no portal"} — mas{" "}
            <b>não</b> as AULAS: o Memberkit continua com o endereço anterior e precisa
            ser ajustado lá. O currículo do aluno, se ele tiver um, também mantém o
            e-mail que ele mesmo digitou.
            {dossie.perfil.tem_conta && " O endereço anterior recebe um aviso do Auth."}
          </p>
        </>
      )}

      {acao.tipo === "primeiro-acesso" && (
        <p className="mt-3 rounded-cf bg-alerta-fundo px-3 py-2 text-sm leading-relaxed text-alerta-tinta">
          O e-mail vai do Auth direto para {dossie.perfil.email}
          {dossie.perfil.tem_conta
            ? " — ele já tem conta, então é um link de redefinição de senha."
            : " — ele ainda não tem conta, então é um convite para criar a senha."}{" "}
          O link não aparece nesta tela: quem tem um link de redefinição entra na
          conta.
        </p>
      )}

      {acao.tipo === "senha-liberada" && (
        <p className="mt-3 rounded-cf bg-alerta-fundo px-3 py-2 text-sm leading-relaxed text-alerta-tinta">
          Para quem não recebe o e-mail. Por <b>1 hora</b> e <b>uma vez só</b>, o próprio
          aluno cria a senha em <code className="break-all">{enderecoDaSenhaLiberada()}</code>,
          digitando o e-mail {dossie.perfil.email}. Você não vê nem digita a senha. Libere
          só durante a conversa com o aluno: nessa hora, quem souber o e-mail dele e chegar
          antes cria a senha.
        </p>
      )}

      {acao.tipo === "liberar" && (
        <div className="mt-4">
          <label htmlFor="produto" className="block text-xs font-bold tracking-wide text-cinza uppercase">
            Produto
          </label>
          <select
            id="produto"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            required
            className="mt-1 w-full max-w-md rounded-cf border border-borda bg-superficie px-3 py-2 text-sm text-tinta"
          >
            <option value="">escolha o produto</option>
            {produtos.map((pr) => (
              <option key={pr.slug} value={pr.slug}>
                {pr.nome}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-cinza">Para {dossie.perfil.email}.</p>
        </div>
      )}

      <div className="mt-4">
        <label htmlFor="motivo" className="block text-xs font-bold tracking-wide text-cinza uppercase">
          Motivo (obrigatório)
        </label>
        <textarea
          id="motivo"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={2}
          required
          placeholder="ex.: prazo lançado errado na venda 1234, confirmado com o financeiro"
          className="mt-1 w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-sm text-tinta placeholder:text-cinza/70"
        />
        <p className="mt-1 text-xs leading-relaxed text-cinza">
          Fica gravado com seu nome. Escreva para quem vai ler isso em seis meses
          sem lembrar do caso.
        </p>
      </div>

      {erro && <p className="mt-3 rounded-cf bg-erro-fundo px-3 py-2 text-sm text-erro-tinta">{erro}</p>}

      <div className="mt-4 flex flex-wrap gap-2">
        <button type="submit" disabled={salvando || motivo.trim() === ""} className="pill-cta">
          {salvando ? "Gravando…" : "Confirmar"}
        </button>
        <button type="button" onClick={() => onFim(null)} className="btn-contorno">
          Desistir
        </button>
      </div>
    </form>
  );
}

/** O endereço que o atendente passa ao aluno: o do hub em que ele está. */
function enderecoDaSenhaLiberada(): string {
  return `${window.location.origin}/senha-liberada`;
}

function textoDaSenhaLiberada(s: SituacaoDaSenhaLiberada | undefined): string {
  if (s === undefined) return "—";
  if (s === null) return "nunca liberada";
  if (s.valendo) return `liberada até ${dataHora(s.vale_ate)}${s.liberada_por ? `, por ${s.liberada_por}` : ""}`;
  if (s.usada_em) return `usada em ${dataHora(s.usada_em)}`;
  if (s.cancelada_em) return `cancelada em ${dataHora(s.cancelada_em)}`;
  return `venceu sem uso em ${dataHora(s.vale_ate)}`;
}

function CampoTexto({
  rotulo,
  id,
  valor,
  onMuda,
  dica,
}: {
  rotulo: string;
  id: string;
  valor: string;
  onMuda: (v: string) => void;
  dica?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-bold tracking-wide text-cinza uppercase">
        {rotulo}
      </label>
      <input
        id={id}
        value={valor}
        onChange={(e) => onMuda(e.target.value)}
        className="mt-1 w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-sm text-tinta"
      />
      {dica && <p className="mt-1 text-xs text-cinza">{dica}</p>}
    </div>
  );
}

/**
 * "Criar usuário" — o cadastro, não a conta de login.
 *
 * A distinção é a que mais confunde nesta tela, e por isso está escrita nela: no
 * hub, criar cadastro é criar a linha do aluno; a **conta** nasce quando a pessoa
 * define a senha, pelo convite. São dois passos, e o segundo é "Liberar primeiro
 * acesso" no dossiê.
 *
 * E-mail que já existe não é erro: abre o dossiê de quem existe. O operador que
 * digita um e-mail repetido quase sempre queria justamente esse aluno.
 */
function CriarCadastro({
  onCriado,
}: {
  onCriado: (profileId: string, jaExistia: boolean) => Promise<boolean> | void;
}) {
  const [email, setEmail] = useState("");
  const [nome, setNome] = useState("");
  const [motivo, setMotivo] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setSalvando(true);
    const r = await criarAluno(email, nome, motivo);
    setSalvando(false);
    if (!r.ok) return setErro(r.mensagem);
    if (!r.res.criou) {
      setErro(`${r.res.motivo_do_nao ?? "já existe"} — abrindo o cadastro existente.`);
    }
    await onCriado(r.res.profile_id, !r.res.criou);
  }

  return (
    <form onSubmit={enviar} className="surface border-laranja/40 p-4 sm:p-5">
      <h2 className="font-display text-base font-bold text-titulo">Criar cadastro</h2>
      <p className="mt-1 max-w-2xl text-sm leading-relaxed text-cinza">
        Cria a linha do aluno, não a conta de login. A conta nasce quando ele define
        a senha — depois de criar, use "Liberar primeiro acesso" para mandar o
        convite.
      </p>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="novo-email" className="block text-xs font-bold tracking-wide text-cinza uppercase">
            E-mail
          </label>
          <input
            id="novo-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="off"
            className="mt-1 w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-sm text-tinta"
          />
          <p className="mt-1 text-xs text-cinza">É a chave que liga o aluno às compras dele.</p>
        </div>
        <div>
          <label htmlFor="novo-nome" className="block text-xs font-bold tracking-wide text-cinza uppercase">
            Nome
          </label>
          <input
            id="novo-nome"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            required
            className="mt-1 w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-sm text-tinta"
          />
        </div>
      </div>

      {/* As caixas Admin/Suporte da tela do Bubble SAÍRAM na 000297: criar
          alguém já com papel era a promoção por outra porta, e desde então o
          acesso ao painel se dá só em Admin › Permissões (o banco recusa papel
          aqui, até para o master). */}

      <div className="mt-4">
        <label htmlFor="novo-motivo" className="block text-xs font-bold tracking-wide text-cinza uppercase">
          Motivo (obrigatório)
        </label>
        <textarea
          id="novo-motivo"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={2}
          required
          placeholder="ex.: aluno da turma presencial, matrícula fechada fora das plataformas"
          className="mt-1 w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-sm text-tinta placeholder:text-cinza/70"
        />
      </div>

      {erro && <p className="mt-3 rounded-cf bg-erro-fundo px-3 py-2 text-sm text-erro-tinta">{erro}</p>}

      <button type="submit" disabled={salvando || motivo.trim() === ""} className="pill-cta mt-4">
        {salvando ? "Criando…" : "Criar cadastro"}
      </button>
    </form>
  );
}
